import React from 'react';
import {
  HighPriorityIcon,
  MediumPriorityIcon,
  LowPriorityIcon
} from '../icons/ViewTicketsIcons';
import { getStatusColor, highlightSearchText } from '../../utils/viewTicketsUtils';

// Helper component to render highlighted text
const HighlightedText = ({ text, searchQuery }) => {
  const highlighted = highlightSearchText(text, searchQuery);
  
  if (typeof highlighted === 'string' || !highlighted.hasMatch) {
    return highlighted;
  }
  
  return (
    <>
      {highlighted.beforeMatch}
      <span style={{ backgroundColor: '#ffff00', fontWeight: 'bold' }}>
        {highlighted.match}
      </span>
      {highlighted.afterMatch}
    </>
  );
};

const getPriorityIcon = (priority) => {
  switch (priority) {
    case 'HIGH':
      return <HighPriorityIcon className="w-5 h-5 text-red-600 animate-pulse" />;
    case 'MEDIUM':
      return <MediumPriorityIcon className="w-5 h-5 text-orange-600 animate-pulse" />;
    case 'LOW':
      return <LowPriorityIcon className="w-5 h-5 text-green-600" />;
    default:
      return null;
  }
};

const TicketsTable = ({
  filteredTickets,
  page,
  rowsPerPage,
  searchQuery,
  onViewDetails
}) => {
  return (
    <div className="max-h-[calc(100vh-250px)] overflow-auto relative">
      <table className="w-full">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr className="border-b-2 border-blue-200">
            <th className="px-4 py-3 text-left text-sm font-bold text-blue-600 font-poppins">
              Ticket ID
            </th>
            <th className="px-4 py-3 text-left text-sm font-bold text-blue-600 font-poppins">
              Tracking ID
            </th>
            <th className="px-4 py-3 text-left text-sm font-bold text-blue-600 font-poppins">
              Category
            </th>
            <th className="px-4 py-3 text-left text-sm font-bold text-blue-600 font-poppins">
              Requester
            </th>
            <th className="px-4 py-3 text-left text-sm font-bold text-blue-600 font-poppins">
              Priority
            </th>
            <th className="px-4 py-3 text-left text-sm font-bold text-blue-600 font-poppins">
              Status
            </th>
            <th className="px-4 py-3 text-left text-sm font-bold text-blue-600 font-poppins">
              Created At
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredTickets.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center py-8">
                <p className="text-gray-500 font-poppins">
                  No matching tickets found
                </p>
              </td>
            </tr>
          ) : (
            filteredTickets
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((ticket) => (
                <tr 
                  key={ticket.id}
                  onClick={() => onViewDetails(ticket)}
                  className="border-b border-gray-200 hover:bg-blue-50 cursor-pointer transition-all duration-200 hover:shadow-sm"
                >
                  <td className="px-4 py-3 font-poppins">
                    #<HighlightedText text={ticket.id} searchQuery={searchQuery} />
                  </td>
                  <td className="px-4 py-3 font-poppins">
                    <HighlightedText text={ticket.trackingId} searchQuery={searchQuery} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-poppins text-sm">
                        <HighlightedText text={ticket.category.replace(/_/g, ' ')} searchQuery={searchQuery} />
                      </span>
                      {/* Display appropriate subcategory based on ticket type */}
                      {ticket.category === 'ACCOUNT_MANAGEMENT' && ticket.details?.accountType && (
                        <span className="text-xs text-gray-500 font-poppins">
                          {ticket.details.accountType}
                          {ticket.details?.actionType && ` (${ticket.details.actionType})`}
                        </span>
                      )}
                      {ticket.category === 'DOCUMENT_UPLOAD' && ticket.details?.documentType && (
                        <span className="text-xs text-gray-500 font-poppins">
                          {ticket.details.documentType}
                        </span>
                      )}
                      {ticket.category === 'TECHNICAL_ASSISTANCE' && ticket.details?.taType && (
                        <span className="text-xs text-gray-500 font-poppins">
                          {ticket.details.taType}
                        </span>
                      )}
                      {ticket.category === 'TROUBLESHOOTING' && ticket.details?.typeOfEquipment && (
                        <span className="text-xs text-gray-500 font-poppins">
                          {ticket.details.typeOfEquipment}
                          {ticket.details?.modelOfEquipment && ` - ${ticket.details.modelOfEquipment}`}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-poppins text-sm">
                        <HighlightedText text={ticket.requester?.name || 'Unknown'} searchQuery={searchQuery} />
                      </span>
                      <span className="text-xs text-gray-500 font-poppins">
                        <HighlightedText text={ticket.requester?.email || 'No email provided'} searchQuery={searchQuery} />
                      </span>
                      {ticket.requester?.department && (
                        <span className="text-xs text-gray-500 font-poppins">
                          <HighlightedText text={ticket.requester.department} searchQuery={searchQuery} />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 font-poppins">
                      {getPriorityIcon(ticket.priority)}
                      <span><HighlightedText text={ticket.priority} searchQuery={searchQuery} /></span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                      <HighlightedText text={ticket.status.replace('_', ' ')} searchQuery={searchQuery} />
                    </span>
                  </td>
                  <td className="px-4 py-3 font-poppins">
                    {new Date(ticket.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TicketsTable;
